// Thin fetch client for the hidden drink-receipt points game. Same
// same-origin/relative-path convention as pollApi.ts. The photo goes up
// as base64 JSON (core has no multipart parser installed).
import { getApiBaseUrl } from './api';

export interface ReceiptSummary {
  merchant_name: string | null;
  doc_number: string;
  date: string;
  time: string | null;
  total_eur: number;
}

export interface ScanResult {
  token: string;
  summary: ReceiptSummary;
  villages: string[];
}

export interface ReceiptStats {
  totals: Record<string, number>;
  receipt_count: number;
}

export interface ConfirmResult {
  status: string;
  stats: ReceiptStats;
}

export class ReceiptApiError extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

const base = () => getApiBaseUrl();

async function throwApiError(r: Response): Promise<never> {
  let code = 'error';
  let message = `Errore ${r.status}`;
  try {
    const body = await r.json();
    if (body?.detail?.code) {
      code = String(body.detail.code);
      message = String(body.detail.message);
    } else if (body?.detail) {
      message = String(body.detail);
    }
  } catch {
    /* non-JSON error body */
  }
  throw new ReceiptApiError(code, message);
}

// Gemini downscales anything larger than ~3072px on its side, so bigger
// uploads only waste festival-grade uplink (a full iPhone photo is
// 6-11 MB of base64; resized it's well under 1 MB). Re-encoding to JPEG
// also sidesteps HEIC entirely.
const MAX_SIDE = 3072;
const JPEG_QUALITY = 0.85;

function downscaleToJpegBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('canvas non disponibile'));
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('immagine non leggibile'));
    };
    img.src = url;
  });
}

export async function scanReceipt(file: File): Promise<ScanResult> {
  const image_base64 = await downscaleToJpegBase64(file);
  const r = await fetch(`${base()}/api/receipts/scan`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      image_base64,
      mime_type: 'image/jpeg',
    }),
  });
  if (!r.ok) await throwApiError(r);
  return r.json();
}

export async function confirmReceipt(
  token: string,
  borgo: string,
): Promise<ConfirmResult> {
  const r = await fetch(`${base()}/api/receipts/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, borgo }),
  });
  if (!r.ok) await throwApiError(r);
  return r.json();
}

export async function getReceiptStats(): Promise<ReceiptStats> {
  const r = await fetch(`${base()}/api/receipts/stats`);
  if (!r.ok) await throwApiError(r);
  return r.json();
}
